<template>
  <div class="flex flex-col items-start gap-2 self-stretch">
    <UITextXsMedium> {{ t("common.survey.layoutType") }}</UITextXsMedium>
    <div class="flex items-start gap-3 self-stretch">
      <div class="layout-container" @click="handleLayoutChange(1)">
        <div class="layout-card" :class="{ 'layout-card-active': currentLayout === 1 }">
          <div class="layout-card-icon">
            <OneQuestionIcon></OneQuestionIcon>
          </div>
        </div>
        <UITextXsMedium>{{ t("common.survey.oneQuestionPerPage") }}</UITextXsMedium>
      </div>
      <div class="layout-container" @click="handleLayoutChange(2)">
        <div class="layout-card" :class="{ 'layout-card-active': currentLayout === 2 }">
          <div class="layout-card-icon">
            <ManyQuestionsIcon></ManyQuestionsIcon>
          </div>
        </div>
        <UITextXsMedium>{{ t("common.survey.allQuestionsOnePage") }}</UITextXsMedium>
      </div>
    </div>
  </div>
  <AlertModal
    :show-modal-popup="openAlertModal"
    :title="titleMessage"
    :description="descriptionMessage"
    :positive-text="t('common.proceed')"
    :negative-text="t('common.cancel')"
    header-type="warning"
    footer-type="warning"
    @pop:change="handleModalCancel"
    @pop:positive="handleModalConfirm"
    @pop:negative="handleModalCancel"
  />
</template>

<script setup lang="ts">
import ManyQuestionsIcon from "@/assets/icons/ManyQuestions.svg";
import OneQuestionIcon from "@/assets/icons/OneQuestion.svg";
import { useAppStore } from "@/store/app";
import {
  convertToOffState,
  mergeAllFieldsInSlide,
  splitOneFieldPerSlide,
} from "@/util/oneQuestionMethods";
import { UITextXsMedium } from "@gohighlevel/ghl-ui";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import AlertModal from "../AlertModal.vue";

const { t } = useI18n();
const store = useAppStore();
const openAlertModal = ref(false);
const pendingLayoutIndex = ref<number>(0);

/**
 * Computed property for the current survey layout
 * @returns {number} The current layout type (1 for one question per page, 2 for all questions on one page)
 */
const currentLayout = computed(() => {
  return store.formAction.fieldSettingEnable ? 1 : 2;
});

/**
 * Computed property for the alert modal title
 * @returns {string} The title message based on the pending layout change
 */
const titleMessage = computed(() => {
  return pendingLayoutIndex.value === 1
    ? t("survey.switchToOneQuestionAtTimeOn")
    : t("survey.switchToOneQuestionAtTimeOff");
});

/**
 * Computed property for the alert modal description
 * @returns {string} The description message based on the pending layout change
 */
const descriptionMessage = computed(() => {
  return pendingLayoutIndex.value === 1
    ? t("survey.switchToOneQuestionAtTimeOnWarning")
    : t("survey.switchToOneQuestionAtTimeOffWarning");
});

/**
 * Handles slide management when switching between one question per page and all questions on one page
 *
 * @param {boolean} isOneQuestionEnabled - Whether one question per page mode is enabled
 */
const slideManageV2 = async (isOneQuestionEnabled: boolean) => {
  const tempKey = "survey_slides_" + store.surveyId;
  if (isOneQuestionEnabled) {
    saveToLocalStorage(tempKey, store.slides);
    const mergeSlide = mergeAllFieldsInSlide(store.slides);
    store.slides = splitOneFieldPerSlide(mergeSlide, true);
  } else {
    const ogSlides = getFromLocalStorage(tempKey);
    const oneQuestionSlides = store.slides;
    const originalSlides = convertToOffState(ogSlides, oneQuestionSlides);
    if (originalSlides.length) {
      store.slides = originalSlides;
    }
  }
};

/**
 * Removes jumpTo conditional logic when switching layout modes
 * This is necessary because jumpTo conditions are only valid in one question at a time mode
 */
const removeJumpToConditions = () => {
  if (store.builderConfig.conditionalLogic && store.builderConfig.conditionalLogic.length > 0) {
    store.builderConfig.conditionalLogic = store.builderConfig.conditionalLogic.filter(
      (logic) => logic.outcome?.type !== "jumpTo"
    );
    store.anyUnsavedChanges = true;
  }
};

/**
 * Handles the layout change request by showing confirmation modal
 * @param {number} layoutIndex - The layout type to change to (1 for one question per page, 2 for all questions on one page)
 */
const handleLayoutChange = (layoutIndex: number) => {
  // Check if the layout is actually changing
  if (layoutIndex === currentLayout.value) {
    return;
  }

  // Store the pending layout change
  pendingLayoutIndex.value = layoutIndex;

  // Show the confirmation modal
  openAlertModal.value = true;
};

/**
 * Performs the actual layout change after confirmation
 * @param {number} layoutIndex - The layout type to change to
 */
const performLayoutChange = async (layoutIndex: number) => {
  const isOneQuestionPerPage = layoutIndex === 1;

  // Update the fieldSettingEnable flag as the primary control
  store.formAction.fieldSettingEnable = isOneQuestionPerPage;

  // Update the layout in the store for backwards compatibility
  store.builderConfig.layout = layoutIndex;

  // Handle slide management if we're in a survey
  if (store.surveyId) {
    await slideManageV2(isOneQuestionPerPage);
  }

  // Remove jumpTo conditions when switching layouts
  removeJumpToConditions();

  // Mark changes and add to history
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

/**
 * Handles modal cancellation
 */
const handleModalCancel = () => {
  // Reset pending layout index
  pendingLayoutIndex.value = 0;

  // Close alert modal
  openAlertModal.value = false;
};

/**
 * Handles modal confirmation
 */
const handleModalConfirm = () => {
  // Perform the layout change with the pending index
  performLayoutChange(pendingLayoutIndex.value);

  // Reset pending layout index
  pendingLayoutIndex.value = 0;

  // Close alert modal
  openAlertModal.value = false;
};

const saveToLocalStorage = (key: string, data: any) => {
  localStorage.setItem(key, JSON.stringify(data));
};

const getFromLocalStorage = (key: string) => {
  return JSON.parse(localStorage.getItem(key) || "[]");
};
</script>

<style scoped>
.layout-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1 0 0;
  cursor: pointer;
}

.layout-card {
  display: flex;
  height: 100px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #eaecf0;
}
.layout-card-active {
  background: #f5f8ff;
  border: 2px solid #155eef;
}
.layout-card-icon {
  padding: 0px 16px;
}
</style>
